const BASE = '/api'

export interface AckResponse {
  ack: 0 | 1
  error?: Array<{ code: string; message: string; field?: string }>
  warning?: Array<{ code: string; message: string }>
  [key: string]: unknown
}

/**
 * Ergebnisform der Batch-Write-Routen (`campaigns`, `keywords`, `ad-groups`) — im
 * Erfolgsfall die Nutzlast des 200-Acks, im 502-Fall der Inhalt von `body.result`.
 * Bewusst hier und nicht je API-Modul: es ist EINE Form, die sich mehrere Module
 * teilen (`keywords.ts` und `campaigns.ts` re-exportieren sie namensgleich).
 */
export interface BatchWriteFailedItem {
  id: number
  error: string
  policyDenial?: boolean
  reconcileRequired?: boolean
}

export interface BatchWriteResult {
  success: number
  failed: BatchWriteFailedItem[]
}

export class ApiError extends Error {
  // true wenn der Fehler dem Operator bereits anderweitig sichtbar gemacht wurde —
  // entweder als Toast durch den Write-Denial-Handler unten (403/423) oder durch
  // eine Liste, die den Fall selbst darstellt (`markReconcileHandledByList`).
  // Der MutationCache-Handler in main.tsx toastet ihn dann nicht erneut.
  public toasted = false

  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    // M9 (Scheibe E): der geparste Antwort-Body. Bis hierher verwarf der Client
    // alles ausser Status/Code/Meldung — das `result` eines 502-Batchergebnisses
    // (Teilerfolge + pruefbeduerftige Eintraege) ging dabei verloren.
    public readonly body?: AckResponse,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// Die beiden Ergebnis-Buckets der Batch-Services, gespiegelt aus
// `backend/src/shared/utils/reply-reconcile.ts`: `failed[]` bei
// campaigns/keywords/ad-groups, `results[]` bei negatives/targets. Die
// `Array.isArray`-Pruefung ist kein Zierrat — `batchUpdateProductTargetState`
// fuehrt BEIDE Schluessel, `failed` dort aber als ZAHL.
const RECONCILE_BUCKETS = ['failed', 'results'] as const

function isReconcileEntry(entry: unknown): boolean {
  return (
    typeof entry === 'object' &&
    entry !== null &&
    'reconcileRequired' in entry &&
    entry.reconcileRequired === true
  )
}

/**
 * Wie viele Eintraege dieses Fehlers haben einen ungeklaerten Amazon-Zustand?
 *
 * Der Diskriminator zwischen den ZWEI 502-Formen des Backends:
 *  - FORM 1, Batch-Write-Routen (`ackOrReconcile`, M22): `error[0].code` traegt
 *    den STRING `RECONCILE_REQUIRED`, die Nutzdaten stehen unter `result`.
 *    Nur diese Form ergibt einen Wert > 0 — sie ist die, die eine Liste
 *    darstellen kann.
 *  - FORM 2, `reply.nack(502, …)` (Promotions-Apply M23, Experiment-Execute
 *    M20/M21): Zahl-Code, KEIN `result`. Ergibt 0 — hier gibt es nichts zu
 *    zaehlen und nichts zu bebannern; der gewohnte Toast bleibt zustaendig.
 *
 * 0 heisst also immer „nicht dieser Fall" und nie „Fall ohne Eintraege": ein
 * 502 aus FORM 1 entsteht erst ab einem pruefbeduerftigen Eintrag. Faellt der
 * Body unerwartet aus der Form, ist 0 die sichere Antwort — dann greift der
 * generische Toast-Pfad, statt dass der Hinweis ganz verschwindet.
 */
export function reconcileRequiredCount(err: unknown): number {
  if (!(err instanceof ApiError)) return 0
  if (err.status !== 502 || err.code !== 'RECONCILE_REQUIRED') return 0

  const result = err.body?.result
  if (typeof result !== 'object' || result === null) return 0

  let count = 0
  for (const bucket of RECONCILE_BUCKETS) {
    const entries = (result as Record<string, unknown>)[bucket]
    if (!Array.isArray(entries)) continue
    count += entries.filter(isReconcileEntry).length
  }
  return count
}

/**
 * Ist dieser Fehler ein 502 mit ungeklaertem oder bestaetigt-aber-unverbuchtem
 * Amazon-Zustand — unabhaengig davon, ob Nutzdaten dranhaengen?
 *
 * Ergaenzt `reconcileRequiredCount`, ersetzt es NICHT. Der Zaehler beantwortet
 * „wie viele Eintraege einer LISTE sind pruefbeduerftig" und ist per Konstruktion
 * 0, wo es keine `result`-Ebene gibt. Diese Funktion beantwortet die andere Frage:
 * „ist dieser EINE Write in den Reconcile-Zustand geraten".
 *
 * Gebraucht wird sie fuer die Einzel-Write-Routen aus Scheibe H/O
 * (`negatives/routes.ts`, acht Stellen): die bauen ihren 502-Envelope direkt und
 * setzen den STRING-Code `RECONCILE_REQUIRED`, fuehren aber KEIN `result` — es gibt
 * bei einem Einzel-Write kein Teilergebnis zu berichten. Der Zaehler ergibt dort
 * also 0, obwohl der Fall vorliegt.
 *
 * Bewusst NICHT am Meldungstext erkannt: den gibt es in zwei Wortlauten
 * („lokal nicht persistiert" im Batch, „lokale Verbuchung unvollstaendig" im
 * Einzelfall, siehe wiki/08-glossar.md). Der String-Code ist der belastbare
 * Vertrag — ein gewoehnliches `reply.nack(502, …)` traegt die ZAHL 502 und faellt
 * hier korrekt durch.
 */
export function isReconcileRequiredError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 502 && err.code === 'RECONCILE_REQUIRED'
}

/**
 * Markiert einen FORM-1-502 als bereits sichtbar gemacht, damit der globale
 * MutationCache-Handler (main.tsx) keinen zweiten, unschaerferen Hinweis toastet.
 *
 * Aufzurufen ausschliesslich in der `mutationFn` einer Mutation, deren Liste den
 * Fall wirklich anzeigt — das ist die einzige Stelle, die VOR dem
 * MutationCache-Handler laeuft (`mutationCache.config.onError` kommt vor
 * `options.onError` und vor den `mutate`-Callbacks). Alles andere bleibt
 * unberuehrt: FORM 2 und jeder sonstige Fehler behalten ihren Toast.
 *
 * VORBEDINGUNG, nicht optional (Fix-Runde 1, F1): Der Aufrufer muss vorher
 * pruefen, dass sein Konsument NOCH LEBT. Wer hier blind markiert, wuergt den
 * Toast auch dann ab, wenn die Liste laengst unmountet ist (Operator hat
 * waehrend eines langsamen Batch-Writes die Seite gewechselt) — dann zeigt
 * niemand mehr etwas an und der 502 verschwindet spurlos. Ein verschluckter
 * Hinweis ist schlimmer als ein doppelter; im Zweifel NICHT markieren.
 */
export function markReconcileHandledByList(err: unknown): void {
  if (err instanceof ApiError && reconcileRequiredCount(err) > 0) err.toasted = true
}

// FC-Finding #3 (Integration): Writes ausserhalb des TanStack-Mutation-Layers
// (z.B. Sync.tsx api.put/api.post) erreichen den MutationCache-Toast nicht.
// Der Client meldet 403/423-Write-Denials daher selbst an einen injizierten
// Handler (Verdrahtung mit dem Toast-Store in main.tsx — kein Import-Zyklus,
// da lib/toast.ts seinerseits ApiError aus dieser Datei importiert).
type WriteDenialHandler = (error: ApiError) => void
let writeDenialHandler: WriteDenialHandler | null = null

export function setWriteDenialHandler(handler: WriteDenialHandler | null): void {
  writeDenialHandler = handler
}

function reportWriteDenial(method: string | undefined, err: ApiError): void {
  const isWrite = method !== undefined && method.toUpperCase() !== 'GET'
  if (!isWrite) return
  if (err.status !== 403 && err.status !== 423) return
  err.toasted = true
  writeDenialHandler?.(err)
}

function getToken(): string | null {
  return localStorage.getItem('amz_ads_token')
}

export function setToken(token: string): void {
  localStorage.setItem('amz_ads_token', token)
}

export function clearToken(): void {
  localStorage.removeItem('amz_ads_token')
  localStorage.removeItem('amz_ads_user')
}

export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
  requireAckOne = false,
): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> | undefined),
  }
  // Content-Type nur setzen wenn Body vorhanden — sonst lehnt Fastify
  // leere Requests mit Content-Type=application/json ab (DELETE etc.)
  if (options.body !== undefined && options.body !== null) {
    headers['Content-Type'] ??= 'application/json'
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${BASE}${path}`, { ...options, headers })

  if (res.status === 401) {
    clearToken()
    window.location.href = '/login'
    throw new ApiError(401, 'UNAUTHORIZED', 'Sitzung abgelaufen')
  }

  const body = (await res.json()) as AckResponse

  // Opt-in only: legacy modules retain their existing envelope behaviour.
  // Inspect before stripping ACK; malformed success cannot prove a mutation.
  if (requireAckOne && res.ok && (!body || body.ack !== 1 ||
    (body.error !== undefined && (!Array.isArray(body.error) || body.error.length > 0)))) {
    throw new Error('Ungültiger M2c-ACK — Ergebnis unbekannt; Klärung erforderlich.')
  }

  if (!res.ok || body.ack === 0) {
    const err = body.error?.[0]
    const apiError = new ApiError(
      res.status,
      err?.code ?? 'API_ERROR',
      err?.message ?? `HTTP ${res.status}`,
      body,
    )
    reportWriteDenial(options.method, apiError)
    throw apiError
  }

  // Backend ACK-Envelope verbreitet Daten als Top-Level-Properties
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { ack, warning, error, ...data } = body
  return data as T
}

export async function apiUpload<T = unknown>(
  path: string,
  formData: FormData,
): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers,
    body: formData,
  })

  if (res.status === 401) {
    clearToken()
    window.location.href = '/login'
    throw new ApiError(401, 'UNAUTHORIZED', 'Sitzung abgelaufen')
  }

  const body = (await res.json()) as AckResponse

  if (!res.ok || body.ack === 0) {
    const err = body.error?.[0]
    const apiError = new ApiError(
      res.status,
      err?.code ?? 'API_ERROR',
      err?.message ?? `HTTP ${res.status}`,
      body,
    )
    reportWriteDenial('POST', apiError)
    throw apiError
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { ack, warning, error, ...data } = body
  return data as T
}

export async function apiDownload(path: string): Promise<Blob> {
  const token = getToken()
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${BASE}${path}`, { method: 'GET', headers })

  if (res.status === 401) {
    clearToken()
    window.location.href = '/login'
    throw new ApiError(401, 'UNAUTHORIZED', 'Sitzung abgelaufen')
  }

  if (!res.ok) {
    throw new ApiError(res.status, 'DOWNLOAD_ERROR', `HTTP ${res.status}`)
  }

  return res.blob()
}

/**
 * Zusaetze fuer LESE-Anfragen: Abbruchsignal und optional strikter ACK. `apiFetch`
 * spreadet `RequestInit` ohnehin in `fetch`, es fehlte allein die Durchreiche.
 * Nur fuer `get` und ausdruecklich lesende POSTs: eine
 * abgebrochene SCHREIB-Anfrage kann serverseitig laengst gewirkt haben —
 * wer sie abbricht, weiss danach weniger als vorher.
 */
type ResponseOptionen = { requireAckOne?: boolean }
type LeseOptionen = ResponseOptionen & { signal?: AbortSignal }

export const api = {
  get: <T>(path: string, optionen: LeseOptionen = {}) =>
    apiFetch<T>(path, {
      method: 'GET',
      // Nur anhaengen, wenn eines da ist: kein `signal: undefined` in den
      // Aufrufen aller anderen Seiten.
      ...(optionen.signal ? { signal: optionen.signal } : {}),
    }, optionen.requireAckOne),
  post: <T>(path: string, body?: unknown, optionen: ResponseOptionen = {}) =>
    apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) }, optionen.requireAckOne),
  /** Nur fuer serverseitig wirkungsfreie Berechnungen, nie fuer Writes. */
  postLesend: <T>(path: string, body: unknown, optionen: LeseOptionen = {}) =>
    apiFetch<T>(path, {
      method: 'POST',
      body: JSON.stringify(body),
      ...(optionen.signal ? { signal: optionen.signal } : {}),
    }, optionen.requireAckOne),
  put: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData) => apiUpload<T>(path, formData),
  download: (path: string) => apiDownload(path),
}
